import { z } from 'zod';

/** Resposta da ferramenta `registrar_itens` (SPEC §24.2). Campos em português
 *  de propósito: é o que o modelo lê no prompt, e traduzir aqui só criaria
 *  duas convenções para a mesma coisa. */
export const AiItemSchema = z.object({
  linhas: z.array(z.number().int()).min(1),
  tipo: z.enum(['tarefa', 'compromisso', 'nota', 'ignorar']),
  transcricao: z.string(),
  texto: z.string(),
  data: z.string().nullable().optional(),
  hora: z.string().nullable().optional(),
  duracao_min: z.number().int().nullable().optional(),
  prazo_texto: z.string().nullable().optional(),
  prioridade: z.number().int().min(1).max(4).optional(),
  responsavel: z.string().nullable().optional(),
  projeto: z.string().nullable().optional(),
  etiquetas: z.array(z.string()).optional(),
  confianca: z.enum(['alta', 'media', 'baixa']),
  duvida: z.string().nullable().optional(),
  // 'privado' vem do símbolo (p): o item vira tarefa normalmente, mas fica
  // fora da ata e de qualquer e-mail (SPEC §9 e §15.4).
  privado: z.boolean().optional(),
  // 'concluida' e 'adiada' são o X e o > desenhados sobre o ☐ de uma tarefa
  // já enviada — a via de reserva do gesto local (SPEC §16.3).
  relacao_anterior: z.enum(['atualiza', 'novo', 'igual', 'concluida', 'adiada']).nullable().optional(),
  item_anterior_id: z.string().nullable().optional(),
});
export type AiItem = z.infer<typeof AiItemSchema>;

export const AiResponseSchema = z.object({
  itens: z.array(AiItemSchema),
  transcricao_linhas: z.array(z.object({ linha: z.number().int(), texto: z.string() })),
  titulo_sugerido: z.string().nullable().optional(),
  // Nomes da linha @@ do cabeçalho. Não geram item; alimentam os
  // participantes da reunião na Fase 5 (SPEC §15.2).
  participantes_escritos: z.array(z.string()).optional(),
});
export type AiResponse = z.infer<typeof AiResponseSchema>;

export type ItemKind = 'task' | 'event' | 'note' | 'ignore';
export type ItemStatus = 'proposed' | 'accepted' | 'rejected' | 'sent' | 'send_failed';
export type Confidence = 'alta' | 'media' | 'baixa';

export const KIND_FROM_PT: Record<AiItem['tipo'], ItemKind> = {
  tarefa: 'task',
  compromisso: 'event',
  nota: 'note',
  ignorar: 'ignore',
};
export const KIND_TO_PT: Record<ItemKind, AiItem['tipo']> = {
  task: 'tarefa',
  event: 'compromisso',
  note: 'nota',
  ignore: 'ignorar',
};

export interface ExtractedItem {
  id: string;
  runId: string;
  pageId: string;
  rows: number[];
  kind: ItemKind;
  transcription: string;
  title: string;
  dueDate: string | null;
  dueTime: string | null;
  durationMin: number | null;
  priority: 1 | 2 | 3 | 4;
  assignee: string | null;
  project: string | null;
  projectId: string | null;
  labels: string[];
  confidence: Confidence;
  doubt: string | null;
  private: boolean;
  relation: 'atualiza' | 'novo' | 'igual' | 'concluida' | 'adiada' | null;
  previousItemId: string | null;
  status: ItemStatus;
  editedByUser: boolean;
  createdAt: string;
  updatedAt: string;
  todoistTaskId?: string | null;
  todoistState?: 'open' | 'completed' | 'deleted' | null;
}

/** Edições que a revisão pode mandar de volta antes de confirmar. */
export const ItemPatchSchema = z.object({
  id: z.string(),
  kind: z.enum(['task', 'event', 'note', 'ignore']).optional(),
  title: z.string().optional(),
  dueDate: z.string().nullable().optional(),
  dueTime: z.string().nullable().optional(),
  durationMin: z.number().int().nullable().optional(),
  priority: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
  assignee: z.string().nullable().optional(),
  projectId: z.string().nullable().optional(),
  project: z.string().nullable().optional(),
  labels: z.array(z.string()).optional(),
  accepted: z.boolean().optional(),
});
export type ItemPatch = z.infer<typeof ItemPatchSchema>;
